import { ActionCardArgsType, ActionCardNavigationArgsType } from "./type";
import useActionCardService from "./service";
import classNames from "classnames";
import { DashboardActionType } from "../../../../../../shared/enums/DashboardActionType";
import React from "react";
import { useCopies } from "../../../../../../shared/hooks/useCopies";
import { useStaticData } from "../../../../../../context/staticData";
import Button from "../../../../../../shared/components/button";
import { getTimelineActivityStatusIndicatorClass, navigateBasedOnDashboardActionType } from "../../util";
import { useNavigate } from "react-router-dom";
import { ActivityAction } from "../../../../../../shared/enums/ActivityAction";
import ToolTip from "../../../../../../shared/components/tooltip";
import Size from "../../../../../../shared/enums/Size";
import ContextEntityType from "shared/enums/ContextEntityType";
import ActionIndicatorIcon from "../actionIndicatorIcon";

export default function ActionCard(props: ActionCardArgsType) {
  const { timelineDashboardActionType, commonDashboardActionType, activityStatus } = useStaticData();
  const dashboardActionTypeCopyMap = React.useMemo(() => {
    return { ...timelineDashboardActionType, ...commonDashboardActionType };
  }, [timelineDashboardActionType, commonDashboardActionType]);
  const className = getTimelineActivityStatusIndicatorClass({ activityStatus: props.action.activityStatus });
  const service = useActionCardService(props);

  return (
    <div className={classNames("dashboard_actions_programTimeline", className, props.customClass)}>
      <ActionIndicatorIcon activityStatus={props.action.activityStatus} />
      <div className="dashboard_actions_programTimeline_status">
        <b>
          <span>{dashboardActionTypeCopyMap[props.action.dashboardActionType]} </span>
          {service.dateToDisplay && <small>({service.dateToDisplay})</small>}
        </b>
      </div>
      <div className="dashboard_actions_programTimeline_actions">
        {props.action.activityStatus && (
          <span className="dashboard_actions_programTimeline_actions_status">
            {activityStatus[props.action.activityStatus]}{" "}
            {props.action.metadata?.message && (
              <span className="dashboard_actions_programTimeline_actions_status--info">{`(${props.action.metadata?.message})`}</span>
            )}
          </span>
        )}
        <ActionCardNavigationHandler
          action={props.action}
          programParticipants={props.programParticipants}
          onClickCallback={props.onActionClickCallback}
          metaData={props.metaData}
        />
      </div>
    </div>
  );
}

function ActionCardNavigationHandler(props: ActionCardNavigationArgsType) {
  const copies = useCopies();

  switch (props.action.dashboardActionType) {
    case DashboardActionType.COMPLETE_ORG_PARTICIPANT_PROFILE:
      const pendingProfileParticipants = props.action.metadata?.pending;
      const isActionPendingForParticipants = parseInt(pendingProfileParticipants) > 0;
      return (
        <React.Fragment>
          {isActionPendingForParticipants && (
            <span>{copies.get("pending.for.participants", { 0: pendingProfileParticipants })}</span>
          )}
          <ActionClickHandler action={props.action} />
        </React.Fragment>
      );
    default:
      return (
        <ActionClickHandler
          action={props.action}
          programParticipants={props.programParticipants}
          onClickCallback={props.onClickCallback}
          metaData={props.metaData}
        />
      );
  }
}

export function ActionClickHandler({ onClickCallback, ...props }: ActionCardNavigationArgsType) {
  const navigate = useNavigate();
  const { activityAction } = useStaticData();
  const copies = useCopies();

  const onActionClickHandler = React.useCallback(
    (dashboardActionType?: DashboardActionType) => {
      navigateBasedOnDashboardActionType({
        navigate,
        dashboardActionType: dashboardActionType || props.action.dashboardActionType,
        customData: {
          entityId: props.action.entityId,
          programType: props.metaData?.programType,
          programParticipants: props.programParticipants,
          programId: props.metaData?.programId,
          programOrganizationId: props.metaData?.programOrganizationId,
          contextEntityId: props.metaData?.programId,
          contextEntityType: ContextEntityType.PROGRAM,
          eventParticipant: props.eventParticipant
        }
      });
      onClickCallback?.();
    },
    [
      navigate,
      props.action.dashboardActionType,
      props.action.entityId,
      props.metaData,
      props.programParticipants,
      props.eventParticipant,
      onClickCallback
    ]
  );

  if (!props.action.activityAction) {
    return null;
  }

  switch (props.action.activityAction) {
    case ActivityAction.MANAGE_PARTICIPANTS:
      return (
        <div className="actionButton">
          <Button.Link
            label={activityAction[props.action.activityAction]}
            onClick={() => onActionClickHandler(DashboardActionType.MANAGE_PARTICIPANTS)}
          />
          <ToolTip size={Size.SMALL} content={copies.get("manage.participant.help.text")} />
        </div>
      );
    default:
      return (
        <div className="actionButton">
          <Button.Link label={activityAction[props.action.activityAction]} onClick={() => onActionClickHandler()} />
        </div>
      );
  }
}
